import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Trophy, Heart, RotateCcw, 
  Clock, Sparkles, Target
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { useToast } from '../../hooks/use-toast';
import api from '../../utils/api';

const GRID_SIZE = 8;
const CANDY_TYPES = ['🍎', '🍊', '🍋', '🍇', '🍓', '🫐'];

const MatchManiaGame = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Game state
  const [grid, setGrid] = useState([]);
  const [score, setScore] = useState(0);
  const [moves, setMoves] = useState(30);
  const [targetScore, setTargetScore] = useState(500);
  const [selectedCell, setSelectedCell] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [combo, setCombo] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [gameWon, setGameWon] = useState(false);
  
  // Session state
  const [session, setSession] = useState(null);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const [endingGame, setEndingGame] = useState(false);

  // Load session on mount
  useEffect(() => {
    const stored = localStorage.getItem('currentGameSession');
    if (stored) {
      const sessionData = JSON.parse(stored);
      if (sessionData.gameSlug === 'match-mania') {
        setSession(sessionData);
        // Adjust difficulty
        switch (sessionData.difficulty) {
          case 'basic': setMoves(999); setTargetScore(300); break;
          case 'advance': setMoves(30); setTargetScore(500); break;
          case 'super': setMoves(20); setTargetScore(700); break;
          default: setMoves(30); setTargetScore(500);
        }
        initializeGrid();
      } else {
        navigate('/games');
      }
    } else {
      navigate('/games');
    }
  }, [navigate]);

  // Timer
  useEffect(() => {
    let timer;
    if (!gameOver && !gameWon && grid.length > 0) {
      timer = setInterval(() => {
        setTimeElapsed(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [gameOver, gameWon, grid.length]);

  // Initialize grid
  const initializeGrid = useCallback(() => {
    const newGrid = [];
    for (let row = 0; row < GRID_SIZE; row++) {
      const gridRow = [];
      for (let col = 0; col < GRID_SIZE; col++) {
        let candy;
        do {
          candy = CANDY_TYPES[Math.floor(Math.random() * CANDY_TYPES.length)];
        } while (
          (col >= 2 && gridRow[col - 1] === candy && gridRow[col - 2] === candy) ||
          (row >= 2 && newGrid[row - 1]?.[col] === candy && newGrid[row - 2]?.[col] === candy)
        );
        gridRow.push(candy);
      }
      newGrid.push(gridRow);
    }
    setGrid(newGrid);
  }, []);

  // Check for matches
  const findMatches = useCallback((currentGrid) => {
    const matches = [];
    
    // Check horizontal matches
    for (let row = 0; row < GRID_SIZE; row++) {
      for (let col = 0; col < GRID_SIZE - 2; col++) {
        const candy = currentGrid[row][col];
        if (candy && candy === currentGrid[row][col + 1] && candy === currentGrid[row][col + 2]) {
          let matchLength = 3;
          while (col + matchLength < GRID_SIZE && currentGrid[row][col + matchLength] === candy) {
            matchLength++;
          }
          for (let i = 0; i < matchLength; i++) {
            matches.push({ row, col: col + i });
          }
          col += matchLength - 1;
        }
      }
    }

    // Check vertical matches
    for (let col = 0; col < GRID_SIZE; col++) {
      for (let row = 0; row < GRID_SIZE - 2; row++) {
        const candy = currentGrid[row][col];
        if (candy && candy === currentGrid[row + 1][col] && candy === currentGrid[row + 2][col]) {
          let matchLength = 3;
          while (row + matchLength < GRID_SIZE && currentGrid[row + matchLength][col] === candy) {
            matchLength++;
          }
          for (let i = 0; i < matchLength; i++) {
            matches.push({ row: row + i, col });
          }
          row += matchLength - 1;
        }
      }
    }

    // Remove duplicates
    return matches.filter((match, index, self) =>
      index === self.findIndex(m => m.row === match.row && m.col === match.col)
    );
  }, []);

  // Remove matches and drop candies
  const removeMatchesAndDrop = useCallback(async (currentGrid, matches) => {
    if (matches.length === 0) return currentGrid;

    const newGrid = currentGrid.map(row => [...row]);
    
    // Calculate score
    const basePoints = matches.length * 10;
    const comboMultiplier = Math.max(1, combo);
    const points = basePoints * comboMultiplier;
    setScore(prev => prev + points);
    setCombo(prev => prev + 1);

    // Remove matched candies
    matches.forEach(({ row, col }) => {
      newGrid[row][col] = null;
    });

    // Drop candies down
    for (let col = 0; col < GRID_SIZE; col++) {
      let writeRow = GRID_SIZE - 1;
      for (let row = GRID_SIZE - 1; row >= 0; row--) {
        if (newGrid[row][col] !== null) {
          if (writeRow !== row) {
            newGrid[writeRow][col] = newGrid[row][col];
            newGrid[row][col] = null;
          }
          writeRow--;
        }
      }
      
      // Fill empty spaces with new candies
      for (let row = writeRow; row >= 0; row--) {
        newGrid[row][col] = CANDY_TYPES[Math.floor(Math.random() * CANDY_TYPES.length)];
      }
    }

    return newGrid;
  }, [combo]);

  // Process matches recursively
  const processMatches = useCallback(async (currentGrid) => {
    let grid = currentGrid;
    let matches = findMatches(grid);
    
    while (matches.length > 0) {
      setIsAnimating(true);
      grid = await removeMatchesAndDrop(grid, matches);
      setGrid([...grid]);
      await new Promise(resolve => setTimeout(resolve, 300));
      matches = findMatches(grid);
    }
    
    setCombo(0);
    setIsAnimating(false);
    return grid;
  }, [findMatches, removeMatchesAndDrop]);

  // Swap candies
  const swapCandies = useCallback(async (row1, col1, row2, col2) => {
    const newGrid = grid.map(row => [...row]);
    
    // Swap
    const temp = newGrid[row1][col1];
    newGrid[row1][col1] = newGrid[row2][col2];
    newGrid[row2][col2] = temp;

    // Check if swap creates a match
    const matches = findMatches(newGrid);
    
    if (matches.length > 0) {
      setGrid(newGrid);
      setMoves(prev => prev - 1);
      await processMatches(newGrid);
    } else {
      // Invalid move - swap back (visual feedback)
      setGrid(newGrid);
      await new Promise(resolve => setTimeout(resolve, 200));
      const revertGrid = grid.map(row => [...row]);
      setGrid(revertGrid);
    }
  }, [grid, findMatches, processMatches]);

  // Handle cell click
  const handleCellClick = (row, col) => {
    if (isAnimating || gameOver || gameWon) return;

    if (selectedCell === null) {
      setSelectedCell({ row, col });
    } else {
      // Check if adjacent
      const isAdjacent = 
        (Math.abs(selectedCell.row - row) === 1 && selectedCell.col === col) ||
        (Math.abs(selectedCell.col - col) === 1 && selectedCell.row === row);

      if (isAdjacent) {
        swapCandies(selectedCell.row, selectedCell.col, row, col);
      }
      setSelectedCell(null);
    }
  };

  // Check game end conditions
  useEffect(() => {
    if (score >= targetScore && !gameWon) {
      setGameWon(true);
    } else if (moves <= 0 && score < targetScore && !gameOver) {
      setGameOver(true);
    }
  }, [score, moves, targetScore, gameWon, gameOver]);

  // End game
  const endGame = async (won) => {
    if (!session || endingGame) return;
    
    setEndingGame(true);

    try {
      const res = await api.post('/games/match-mania/end', {
        session_id: session.sessionId,
        score,
        won,
        time_played_seconds: timeElapsed
      });

      setResult(res.data);
      localStorage.removeItem('currentGameSession');
      
      toast({
        title: won ? "Level Complete!" : "Game Over",
        description: won && res.data.reward_earned > 0 
          ? `You earned ${res.data.reward_earned} AGP!` 
          : res.data.message,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to end game",
        variant: "destructive"
      });
    } finally {
      setEndingGame(false);
    }
  };

  // Handle game end
  useEffect(() => {
    if ((gameOver || gameWon) && !result) {
      endGame(gameWon);
    }
  }, [gameOver, gameWon, result]);

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Result screen
  if (result) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-6 text-center">
            {gameWon ? (
              <>
                <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sparkles className="w-10 h-10 text-yellow-400" />
                </div>
                <h2 className="text-2xl font-bold text-green-400 mb-2">Level Complete!</h2>
              </>
            ) : (
              <>
                <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trophy className="w-10 h-10 text-red-400" />
                </div>
                <h2 className="text-2xl font-bold text-red-400 mb-2">Out of Moves!</h2>
              </>
            )}

            <div className="space-y-3 my-6">
              <div className="flex justify-between p-3 bg-muted rounded-lg">
                <span>Score</span>
                <span className="font-bold">{score}</span>
              </div>
              <div className="flex justify-between p-3 bg-muted rounded-lg">
                <span>Target</span>
                <span className="font-bold">{targetScore}</span>
              </div>
              {result.reward_earned > 0 && (
                <div className="flex justify-between p-3 bg-yellow-500/20 rounded-lg">
                  <span>Reward</span>
                  <span className="font-bold text-yellow-400">+{result.reward_earned} AGP</span>
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => navigate('/games')}>
                Game Zone
              </Button>
              <Button className="flex-1" onClick={() => navigate('/games')}>
                Play Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="match-mania-game">
      {/* HUD */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b">
        <div className="max-w-lg mx-auto px-2 sm:px-4 py-2 sm:py-3">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={() => {
              if (confirm('Exit game?')) {
                endGame(score >= targetScore);
              }
            }}>
              <ArrowLeft className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Exit</span>
            </Button>

            <div className="flex items-center gap-2 sm:gap-3">
              <div className="flex items-center gap-1 sm:gap-2 bg-muted px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm">
                <Trophy className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400" />
                <span className="font-bold">{score}</span>
              </div>

              <div className="flex items-center gap-1 sm:gap-2 bg-muted px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm">
                <Target className="w-3 h-3 sm:w-4 sm:h-4 text-green-400" />
                <span className="font-bold">{targetScore}</span>
              </div>

              {session?.difficulty !== 'basic' && (
                <div className="flex items-center gap-1 sm:gap-2 bg-muted px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs sm:text-sm">
                  <RotateCcw className="w-3 h-3 sm:w-4 sm:h-4 text-blue-400" />
                  <span className="font-bold">{moves}</span>
                </div>
              )}
            </div>

            <div className="text-xs sm:text-sm text-muted-foreground">
              {formatTime(timeElapsed)}
            </div>
          </div>

          {/* Progress bar */}
          <div className="mt-2">
            <div className="h-1.5 sm:h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-green-400 to-emerald-500 transition-all duration-300"
                style={{ width: `${Math.min(100, (score / targetScore) * 100)}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Game Grid */}
      <div className="max-w-sm sm:max-w-md mx-auto p-2 sm:p-4">
        <Card className="p-2 sm:p-4">
          <div 
            className="grid gap-0.5 sm:gap-1"
            style={{ gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)` }}
          >
            {grid.map((row, rowIndex) =>
              row.map((candy, colIndex) => (
                <button
                  key={`${rowIndex}-${colIndex}`}
                  onClick={() => handleCellClick(rowIndex, colIndex)}
                  disabled={isAnimating}
                  className={`
                    aspect-square text-lg sm:text-2xl md:text-3xl rounded-md sm:rounded-lg transition-all duration-200
                    flex items-center justify-center
                    ${selectedCell?.row === rowIndex && selectedCell?.col === colIndex
                      ? 'bg-primary/30 ring-2 ring-primary scale-105 sm:scale-110'
                      : 'bg-muted hover:bg-muted/80'
                    }
                    ${isAnimating ? 'pointer-events-none' : ''}
                  `}
                  data-testid={`cell-${rowIndex}-${colIndex}`}
                >
                  {candy}
                </button>
              ))
            )}
          </div>
        </Card>

        {/* Combo indicator */}
        {combo > 1 && (
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
            <div className="text-4xl font-bold text-yellow-400 animate-bounce">
              Combo x{combo}!
            </div>
          </div>
        )}

        {/* Instructions */}
        <div className="mt-4 text-center text-sm text-muted-foreground">
          <p>Tap two adjacent candies to swap and match 3 or more!</p>
        </div>
      </div>
    </div>
  );
};

export default MatchManiaGame;
